import classNames from "classnames";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { Button } from "components/DesignSystem/Button/Button";
import { ChatIcon } from "components/icons/LeftNav/ChatIcon";
import AddTaskEntityModal from "components/tasks/modal/AddTaskEntityModal";
import { TaskAlarmSlider } from "components/TaskSlider/TaskAlarmSlider";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { MouseEvent, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import { taskAlarmApis } from "store/apis/taskList";
import { BillingInvoice } from "types/Models/billing";
import { ComplianceDetail } from "types/Models/calendar";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";

export enum DEADLINESTATUS {
  FILED = "Filed",
  PROCESSING = "Processing",
  OVERDUE = "Overdue",
  DUE = "Due",
}
enum FILING_STATE {
  NOT_STARTED = "NOT_STARTED",
  ONGOING = "ONGOING",
  COMPLETED = "COMPLETED",
  ADD_DETAILS = "Add Details",
}

export const getCTAText = (status = "NOT_STARTED", filing_state?: string) => {
  if (status === FILING_STATE.NOT_STARTED && !filing_state) {
    return "Start filing";
  }
  if (filing_state === FILING_STATE.ADD_DETAILS) {
    return "Add details";
  }
  return "View filing";
};

const getStatus = ({
  deadline,
  filing_state,
}: {
  deadline: string;
  filing_state: string;
}) => {
  const deadlinePassed = dayjs(deadline).isBefore(dayjs(), "day");
  if (filing_state === FILING_STATE.COMPLETED) {
    return DEADLINESTATUS.FILED;
  }
  if (filing_state === FILING_STATE.NOT_STARTED && !deadlinePassed) {
    return DEADLINESTATUS.DUE;
  }
  if (filing_state === FILING_STATE.ONGOING) {
    return DEADLINESTATUS.PROCESSING;
  }
  if (filing_state === FILING_STATE.NOT_STARTED && deadlinePassed) {
    return DEADLINESTATUS.OVERDUE;
  }

  return DEADLINESTATUS.DUE;
};

export const DeadlineCard = ({
  deadlineData,
}: {
  deadlineData: ComplianceDetail;
}) => {
  const { uuid, deadline, filing_state, entity_id, season, entity_name } =
    deadlineData;
  const {
    title,
    description,
    base_task_template_id: taskTemplateId,
    product_type,
    approx_price,
    product_content_type_id,
  } = deadlineData?.base_task_template_info!;

  const {
    task_id: taskId,
    current_state_name,
    channel_url,
  } = deadlineData?.task_info || {};
  const [alarmInvoice, setAlarmInvoice] = useState<BillingInvoice | null>();
  const group = useCurrentGroupContext();
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId, entities } = group;
  const { isForeignCA } = useRoleBasedView();

  const alarmInvoiceModal = useModal();
  const addTaskModal = useModal();

  const [createProductInvoice, { isLoading: addingProduct }] =
    useAddProductsToCartAndCheckoutMutation();

  const sliderModal = useModal();
  const dispatch = useDispatch();

  const payInkleToLaunchFiling = async () => {
    if (groupId && uuid && product_type && taskTemplateId) {
      try {
        const payload = {
          entity_id: entity_id,
          product_content_type_id,
          product_id: taskTemplateId,
          quantity: 1,
          payment_amount:
            !approx_price || isNaN(Number(approx_price))
              ? 0
              : Number(approx_price),
          season: season,
          alarm_id: uuid,
        };

        const invoiceData = await createProductInvoice({
          groupId: groupId,
          entityId: entity_id,
          productData: [payload],
          from_alarms: true,
        }).unwrap();

        setAlarmInvoice(invoiceData?.invoice);
        alarmInvoiceModal.open();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const closeAlarmInvoiceModal = () => {
    setAlarmInvoice(null);
    alarmInvoiceModal.close();
  };

  const onPaymentSuccess = () => {
    closeAlarmInvoiceModal();
    dispatch(taskAlarmApis.util.invalidateTags([{ type: "TASK_ALARM" }]));
  };

  const deadlinePassed = false;

  const status = useMemo(() => {
    return getStatus({
      deadline,
      filing_state,
    });
  }, [deadline, filing_state]);

  const ctaText = getCTAText(filing_state, current_state_name || "");

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (ctaText === "Start filing") {
      return payInkleToLaunchFiling();
    }
    openLink(`/filings/${taskId}`);
  };

  return (
    <>
      <div
        onClick={sliderModal.open}
        className="t-flex t-justify-between t-p-4 t-items-center t-rounded-md t-border t-border-solid t-border-i-neutral-10"
      >
        <div className="t-flex t-gap-x-4 t-items-center t-flex-grow">
          <div className="t-rounded-md t-min-w-[72px]">
            <div
              className={classNames(
                " t-rounded-t-lg t-border-solid t-min-w-[72px] t-border t-py-1 t-flex t-items-center t-justify-center t-border-b-0",
                {
                  "t-bg-green-10 t-border-green-20":
                    status === DEADLINESTATUS.FILED,
                  "t-bg-blue-10 t-border-blue-20":
                    status === DEADLINESTATUS.PROCESSING,
                  "t-bg-red-10 t-border-red-20":
                    status === DEADLINESTATUS.DUE ||
                    status === DEADLINESTATUS.OVERDUE,
                }
              )}
            >
              <span
                className={classNames("t-text-caption t-font-bold", {
                  "t-text-green-80": status === DEADLINESTATUS.FILED,
                  "t-text-blue-80": status === DEADLINESTATUS.PROCESSING,
                  "t-text-red-80":
                    status === DEADLINESTATUS.DUE ||
                    status === DEADLINESTATUS.OVERDUE,
                })}
              >
                {status}
              </span>
            </div>
            <div className="t-flex t-gap-0 t-flex-col t-rounded-b-lg t-justify-center t-pt-2 t-items-center t-w-full t-border t-border-solid t-border-i-neutral-10 t-border-t-0">
              <p className="t-text-text-60 t-text-subtext-sm t-font-semibold">
                {dayjs(deadline).format("MMM DD")}
              </p>
              <p className="t-text-caption t-text-text-30 t-m-0 t-pb-2 -t-mt-3 t-font-medium">
                {dayjs(deadline).format("YYYY")}
              </p>
            </div>
          </div>
          <div className="t-h-11 t-w-[1px] t-bg-i-neutral-10" />
          <div className="t-flex t-flex-col t-gap-1 t-w-full">
            <p className="t-text-subtitle-sm t-text-text-60 t-m-0">{title}</p>
            <div>
              {entities.length > 1 && (
                <p className="t-text-text-30 t-text-subtext-sm t-m-0 t-mb-2">
                  {entity_name}
                </p>
              )}

              {description && (
                <div
                  className={classNames(
                    "t-flex t-items-center t-cursor-pointer t-w-full"
                  )}
                >
                  <p
                    className={classNames(
                      "t-text-text-30 t-text-body-sm t-tracking-normal t-m-0 t-line-clamp-1 t-max-w-[60%]"
                    )}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="t-flex t-gap-2">
          {ctaText !== "Start filing" && (
            <Button
              onClick={() => {
                openLink(`/filings/${taskId}`);
              }}
              isLoading={addingProduct}
              size="small"
            >
              <ChatIcon />
            </Button>
          )}
          <Button
            disabled={deadlinePassed || addingProduct}
            onClick={handleClick}
            isLoading={addingProduct}
            customType={ctaText === "View filing" ? "secondary" : "primary"}
            size="small"
          >
            {ctaText}
          </Button>
        </div>
      </div>

      {addTaskModal.isOpen && (
        <AddTaskEntityModal
          to="/new-deadlines"
          taskTemplateId={taskTemplateId}
          handleClose={addTaskModal.close}
          disableLaunch={deadlinePassed}
        />
      )}
      {sliderModal.isOpen && uuid && (
        <TaskAlarmSlider
          taskId={uuid}
          onClose={sliderModal.close}
          isOpen={sliderModal.isOpen}
          defaultTab="DETAILS"
          dataType="ALARM"
        />
      )}
      {alarmInvoiceModal.isOpen && Boolean(alarmInvoice) && (
        <CheckoutModal
          entityId={entityId}
          type="invoices"
          onInvoicePaid={onPaymentSuccess}
          open={alarmInvoiceModal.isOpen}
          onClose={closeAlarmInvoiceModal}
          invoices={[alarmInvoice as BillingInvoice]}
          payerUserType={isForeignCA && "FCA"}
        />
      )}
    </>
  );
};
